/**
 * MarketMap — Interactive Leaflet map showing project + comparables + concentric radii.
 *
 * Props:
 *  - projectCoords: { lat, lon } or null
 *  - comparables: Array<{ id, name, lat, lon, avg_unit_price, avg_price_per_m2, absorption_units_month }>
 *  - radiusData: optional — output of /market/radius-analysis (adds circles & distance labels)
 *  - denueEstablishments: optional — array of establishments to overlay (future use)
 *  - height: CSS height (default 420px)
 */
import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon path (Leaflet quirk with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom project marker (teal, market module color)
const projectIcon = L.divIcon({
  className: 'custom-project-marker',
  html: `<div style="
    width: 24px; height: 24px; border-radius: 50%;
    background: #0DBD8B; border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(13,189,139,0.5);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-weight: bold; font-size: 11px;
  ">★</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Comparable marker (indigo, small)
const compIcon = L.divIcon({
  className: 'custom-comp-marker',
  html: `<div style="
    width: 14px; height: 14px; border-radius: 50%;
    background: #6366F1; border: 2px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

const RADIUS_STYLES = {
  1000: { color: '#0DBD8B', fillColor: '#0DBD8B', fillOpacity: 0.06, weight: 1.5, label: '1 km · Competencia directa' },
  3000: { color: '#6366F1', fillColor: '#6366F1', fillOpacity: 0.04, weight: 1.5, dashArray: '4 4', label: '3 km · Mercado cercano' },
  5000: { color: '#94A3B8', fillColor: '#94A3B8', fillOpacity: 0.03, weight: 1, dashArray: '6 6', label: '5 km · Contexto ampliado' },
};

function fmtMoney(n) {
  return n != null ? '$' + new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n) : '—';
}

export function MarketMap({ projectCoords, comparables = [], radiusData, height = 420 }) {
  const validComps = useMemo(
    () => (comparables || []).filter(c => c.lat != null && c.lon != null),
    [comparables]
  );

  const points = useMemo(() => {
    const pts = [];
    if (projectCoords?.lat && projectCoords?.lon) pts.push([projectCoords.lat, projectCoords.lon]);
    validComps.forEach(c => pts.push([c.lat, c.lon]));
    return pts;
  }, [projectCoords, validComps]);

  if (!projectCoords?.lat || !projectCoords?.lon) {
    return (
      <div
        data-testid="market-map-empty"
        className="rounded-xl flex items-center justify-center"
        style={{ height, border: '1px dashed var(--border-default)', background: 'var(--bg-secondary)' }}
      >
        <div className="text-center px-6">
          <div className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Mapa no disponible</div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Geocodifica el proyecto para ver la vista de mapa.</div>
        </div>
      </div>
    );
  }

  const center = [projectCoords.lat, projectCoords.lon];
  const compsWithoutCoords = (comparables || []).length - validComps.length;

  return (
    <div className="rounded-xl overflow-hidden" data-testid="market-map" style={{ border: '1px solid var(--border-default)', height }}>
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Radios */}
        {Object.entries(RADIUS_STYLES).map(([meters, style]) => (
          <Circle
            key={meters}
            center={center}
            radius={Number(meters)}
            pathOptions={{
              color: style.color,
              fillColor: style.fillColor,
              fillOpacity: style.fillOpacity,
              weight: style.weight,
              dashArray: style.dashArray,
            }}
          />
        ))}

        {/* Project marker */}
        <Marker position={center} icon={projectIcon}>
          <Popup>
            <div style={{ minWidth: 160 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>Tu proyecto</div>
              <div style={{ fontSize: 10, color: '#64748B', marginTop: 4 }}>
                {projectCoords.lat.toFixed(4)}, {projectCoords.lon.toFixed(4)}
              </div>
            </div>
          </Popup>
        </Marker>

        {/* Comparable markers */}
        {validComps.map(c => (
          <Marker key={c.id} position={[c.lat, c.lon]} icon={compIcon}>
            <Popup>
              <div style={{ minWidth: 180 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0F172A' }}>{c.name}</div>
                {c.location && <div style={{ fontSize: 10, color: '#64748B' }}>{c.location}</div>}
                <div style={{ marginTop: 6, fontSize: 10, lineHeight: 1.5 }}>
                  {c.avg_unit_price ? <div>Precio: <strong>{fmtMoney(c.avg_unit_price)}</strong></div> : null}
                  {c.avg_price_per_m2 ? <div>$/m²: <strong>{fmtMoney(c.avg_price_per_m2)}</strong></div> : null}
                  {c.absorption_units_month ? <div>Absorción: <strong>{c.absorption_units_month} uds/mes</strong></div> : null}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        <FitBounds points={points} />
      </MapContainer>

      {/* Legend footer */}
      <div className="flex items-center justify-between px-3 py-2 text-[10px]" style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--text-muted)' }}>
          <span className="flex items-center gap-1"><span style={{ width: 8, height: 8, borderRadius: 4, background: '#0DBD8B', display: 'inline-block' }} />Proyecto</span>
          <span className="flex items-center gap-1"><span style={{ width: 6, height: 6, borderRadius: 3, background: '#6366F1', display: 'inline-block' }} />{validComps.length} comparables</span>
          {compsWithoutCoords > 0 && (
            <span style={{ color: 'var(--warning-color)' }} data-testid="map-no-geo-warning">{compsWithoutCoords} sin geocodificar</span>
          )}
        </div>
        <span style={{ color: 'var(--text-muted)' }}>Radios: 1 · 3 · 5 km</span>
      </div>
    </div>
  );
}

export default MarketMap;
